import { Button, Divider, Dropdown, Grid, Input, Popover, Tooltip } from '@enterprise-ui/canvas-ui-react'
import EnterpriseIcon, { ErrorFilledIcon, InfoFilledIcon } from '@enterprise-ui/icons'
import { FileViewUI, sendFileMetrics, useAsset, useFiles, userInfoSelector } from 'store'
import { useEnv } from '@praxis/component-runtime-env'
import FileValidationPopoverContent from './FileValidationPopoverContent'
import FileMetadata from './FileMetadata'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FileView } from '@galleryjs/api-digital-assets'
import { useDrag } from 'react-dnd'
import { useSelector } from 'react-redux'

interface IDraggableFile {
  file: FileViewUI
  title: string
  thumbnailFile: any
  deleteAlertModal: (file: FileView) => void
  getTooltip: (fileName: string, fileExtension?: string) => any
}

const DraggableFile = (props: IDraggableFile) => {
  const THREED_TYPES: string[] = ['glb']
  const { file, title, thumbnailFile, deleteAlertModal, getTooltip } = props
  const {
    bucketType,
    enableFileMetadata,
    metadataSupportedExtensions,
    readOnlyFileClassifications,
    validThumbnailClassifications,
    galleryEventsServer,
    fileMetadataMetricsTopic,
  } = useEnv()
  const { updateAssetThumbnail, downloadFile, selectFile } = useFiles()
  const { assetDetailPageAsset: asset, userAssetPermissions } = useAsset()
  const userInfo = useSelector(userInfoSelector)

  const [, ref] = useDrag({
    type: THREED_TYPES[0],
    item: file,
    canDrag: monitor => {
      // Prevent dragging when interacting with Popover or FileMetadata
      const event = monitor.getInitialClientOffset()
      if (!event) return true
      const target = document.elementFromPoint(event.x, event.y)
      if (!target) return true
      // Check if the target is inside a non-draggable component
      return !target.closest('.non-draggable')
    },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  })

  const { fileSources } = file || {}
  const { fileName, fileExtension, accessUrl } =
    (Array.from(fileSources || [])[0] as {
      fileName: string
      fileExtension: string
      accessUrl: string
    }) || {}

  const draggableCodeBlock = (
    <div key={`drawer-record-fragment-${file.id}`} className="filedrawer-files-table">
      <div className="drawer-expandable-section" key={`drawer-record-${file.id}`}>
        <Grid.Container align="center" justify="space-between" noWrap={true}>
          <Grid.Item xs={title === bucketType.REAL_TIME_ASSET ? 8 : 10}>
            <Input.Checkbox
              key={`drawer-file-${file.id}`}
              id={`${file.id}`}
              data-testid={`drawer_file_checkbox_${file.id}`}
              label={
                <Grid.Container>
                  <Grid.Item xs>
                    <p>{`${fileName + '.' + fileExtension}`}</p>
                  </Grid.Item>
                </Grid.Container>
              }
              className="drawer-file-checkbox"
              checked={file.selected}
              onChange={() => {
                selectFile(file.id)
              }}
            />
          </Grid.Item>

          <Grid.Item className="hc-pl-none" xs>
            <Grid.Container align="center" justify="flex-end">
              {file.validationResult && (
                <Grid.Item className="drawer-file-error">
                  <div className="non-draggable">
                    <Popover
                      size="expanded"
                      type="clickOnly"
                      content={
                        <Grid.Container justify="flex-end">
                          <Grid.Item xs={12}>
                            <FileValidationPopoverContent validationContent={file.validationResult} />
                          </Grid.Item>
                        </Grid.Container>
                      }
                      location="bottom"
                      onRequestOpen={() => {
                        sendFileMetrics(
                          galleryEventsServer,
                          fileMetadataMetricsTopic,
                          file.id,
                          fileName,
                          fileExtension,
                          '',
                          asset,
                          userInfo,
                          'VIEW_DATA',
                          'VALIDATION'
                        )
                      }}
                    >
                      <EnterpriseIcon
                        size="sm"
                        icon={ErrorFilledIcon}
                        className="hc-clr-error"
                        style={{ cursor: 'pointer' }}
                      />
                    </Popover>
                  </div>
                </Grid.Item>
              )}
              {title === bucketType.REAL_TIME_ASSET && getTooltip(fileName) && (
                <Grid.Item>
                  <div className="non-draggable">
                    <Tooltip location="top" tooltip={getTooltip(fileName, fileExtension)}>
                      <EnterpriseIcon
                        className="hc-ml-min vertical-align"
                        icon={InfoFilledIcon}
                        size="sm"
                        style={{ cursor: 'pointer' }}
                      />
                    </Tooltip>
                  </div>
                </Grid.Item>
              )}
              {enableFileMetadata && metadataSupportedExtensions.includes(String(fileExtension).toLowerCase()) && (
                <Grid.Item>
                  <div className="non-draggable">
                    <FileMetadata file={file} />
                  </div>
                </Grid.Item>
              )}
            </Grid.Container>
          </Grid.Item>
          <Grid.Item className="hc-mr-normal">
            <div className="non-draggable">
              <Dropdown location="bottom-right" size="dense" key={`drawer-record-dp-${file.id}`}>
                <Button data-testid="dropdown-button" iconOnly key={`${file.id}`} style={{ border: 'none' }}>
                  <FontAwesomeIcon icon="ellipsis-v" color="grey" />
                </Button>
                <Dropdown.Menu data-testid={`files_kabob_menu_${file.id}`} style={{ zIndex: 100 }}>
                  <Dropdown.MenuItem
                    onClick={(e: Event) => {
                      e.preventDefault()
                      downloadFile(accessUrl)
                    }}
                    data-testid={`files_kabob_menu_download_${file.id}`}
                  >
                    Download
                  </Dropdown.MenuItem>
                  {validThumbnailClassifications.includes(file.classification) &&
                    thumbnailFile?.id !== file.id &&
                    userAssetPermissions.userCanEditAndDeleteAsset && (
                      <Dropdown.MenuItem
                        data-testid={`files_kabob_set_as_default_image_${file.id}`}
                        onClick={(e: Event) => {
                          e.preventDefault()
                          updateAssetThumbnail(file.id, asset!!.id)
                        }}
                      >
                        Set as default image
                      </Dropdown.MenuItem>
                    )}
                  {userAssetPermissions.userCanEditAndDeleteAsset &&
                    !readOnlyFileClassifications.includes(file.classification) &&
                    thumbnailFile?.id !== file.id && (
                      <Dropdown.MenuItem
                        data-testid={`files_kabob_delete_${file.id}`}
                        onClick={(e: Event) => {
                          e.preventDefault()
                          deleteAlertModal(file)
                        }}
                      >
                        Delete
                      </Dropdown.MenuItem>
                    )}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </Grid.Item>
        </Grid.Container>
      </div>
      <Divider />
    </div>
  )

  return <>{title === bucketType.REAL_TIME_ASSET ? <div ref={ref}>{draggableCodeBlock}</div> : draggableCodeBlock}</>
}

export default DraggableFile
